/**
 * COMPOSANT UI: SIDE PANEL
 * Panel latéral réutilisable pour consultation/édition
 * COMPORTEMENT: Push layout (pas d'overlay, décale le contenu)
 */

import { cn } from '@/lib/utils';
import { Trash2, X } from 'lucide-react';
import type { ReactNode } from 'react';

interface SidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl';
  onDelete?: () => void; // Optionnel : si fourni, affiche le bouton de suppression
}

const widthClasses = {
  sm: 'w-80',
  md: 'w-96',
  lg: 'w-[32rem]',
  xl: 'w-[40rem]',
};

export function SidePanel({
  isOpen,
  onClose,
  title,
  children,
  footer,
  width = 'xl',
  onDelete,
}: SidePanelProps) {
  return (
    <div
      className={cn(
        'bg-white border-l border-zinc-200 flex flex-col h-full transition-all duration-300 ease-in-out shrink-0',
        widthClasses[width],
        isOpen ? 'translate-x-0' : 'translate-x-full'
      )}
    >
      {/* Header - Aligné avec PageContent header (h-[100px], p-6 = 24px) */}
      <div className="flex items-center justify-between h-[6.25rem] px-6 py-6 shrink-0">
        <h2 className="text-xl font-semibold leading-7 text-zinc-900">{title}</h2>

        {/* Actions (Delete + Close) */}
        <div className="flex items-center gap-1.5">
          {onDelete && (
            <button
              onClick={onDelete}
              className="flex items-center justify-center size-9 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors"
              type="button"
              aria-label="Supprimer"
            >
              <Trash2 className="size-4 text-zinc-900" />
            </button>
          )}
          <button
            onClick={onClose}
            className="flex items-center justify-center size-9 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors"
            type="button"
            aria-label="Fermer"
          >
            <X className="size-4 text-zinc-900" />
          </button>
        </div>
      </div>

      {/* Separator - Même approche que PageContent */}
      <div className="w-full h-px bg-zinc-200 shrink-0" />

      {/* Body - Scrollable */}
      <div className="flex-grow overflow-y-auto p-6">{children}</div>

      {/* Footer - Aligné avec Pagination (py-4) */}
      {footer && (
        <>
          {/* Separator */}
          <div className="w-full h-px bg-zinc-200 shrink-0" />
          <div className="px-6 py-4 shrink-0">{footer}</div>
        </>
      )}
    </div>
  );
}

interface SidePanelFooterProps {
  onCancel: () => void;
  onSubmit?: () => void;
  cancelLabel?: string;
  submitLabel?: string;
  submitDisabled?: boolean;
  isSubmitting?: boolean;
  formId?: string;
}

export function SidePanelFooter({
  onCancel,
  onSubmit,
  cancelLabel = 'Annuler',
  submitLabel = 'Valider',
  submitDisabled = false,
  isSubmitting = false,
  formId,
}: SidePanelFooterProps) {
  return (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={onCancel}
        className="h-9 px-4 py-2 text-sm font-medium text-zinc-900 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors"
        disabled={isSubmitting}
      >
        {cancelLabel}
      </button>
      <button
        type="submit"
        form={formId}
        onClick={onSubmit}
        disabled={isSubmitting || submitDisabled}
        className="h-9 px-4 py-2 text-sm font-medium text-white bg-zinc-900 rounded-md shadow-md hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Chargement...' : submitLabel}
      </button>
    </div>
  );
}
